import { Note, NoteStatus, NoteType, PostNoteAdd, PostNoteUpdate } from "@/api/dtos";
import { FC, useCallback, useContext, useMemo } from "react";
import InputOnlyField from "../../fields/inputOnly/InputOnlyField";
import Select from "../../inputs/Select";
import Text from "../../inputs/Text";
import noteTypeToText from "@/utilities/configuration/noteTypeToText";
import { AppSettings } from "@/AppSettings";
import Form from "../../Form";
import { Box, Button, Paper, Typography, styled } from "@mui/material";
import FormSubmitButton from "../../FormSubmitButton";
import { useLocation, useNavigate } from "react-router-dom";
import { FormError } from "@/errors";
import { IFormSubmit, IFormSubmitFailed, IFormSubmitValidationFailed, IFormValidate, ValidateOn } from "@ngt/forms-core";
import FormErrorType from "@/types/form/FormErrorType";
import { useSnackbar } from "notistack";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import noteStatusToText from "@/utilities/configuration/noteStatusToText";
import noteStatusToColor from "@/utilities/configuration/noteStatusToColor";

const FormContainer = styled(Box)(({ theme }) => ({ 
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
}));


const FormButtons = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'

}));

const typeOptions = [
    {
        id: NoteType.Annotation,
        name: noteTypeToText(NoteType.Annotation)
    },
    {
        id: NoteType.InternalQuery,
        name: noteTypeToText(NoteType.InternalQuery)
    }, 
    {
        id: NoteType.ProtocolDeviation,
        name: noteTypeToText(NoteType.ProtocolDeviation)
    },
    {
        id: NoteType.ImportantProtocolDeviation,
        name: noteTypeToText(NoteType.ImportantProtocolDeviation)
    },
    {
        id: NoteType.ReasonForChange,
        name: noteTypeToText(NoteType.ReasonForChange)
    },
    {
        id: NoteType.SiteQuery,
        name: noteTypeToText(NoteType.SiteQuery)
    },
    {
        id: NoteType.System,
        name: noteTypeToText(NoteType.System)
    },
]

export interface INoteEditFormProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;
    
    noteId?: number;
    status?: NoteStatus;

    onCancel?: () => void;
}

const NoteEditForm: FC<INoteEditFormProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,

    noteId,
    status,
    onCancel
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const client = useContext(ServiceStackClientContext);
    
    const initialData = useMemo(() => {
        return new PostNoteUpdate({
            trialId,
            masterGroupId,
            coordinatingGroupId,
            institutionId,
            participantId,
            studyEventOid,
            studyEventRepeat,
            caseReportFormOid,
            caseReportFormVersionOid,
            itemGroupName,
            itemGroupRepeat,
            itemName,

            noteId,
            status
        })
    }, [
        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat,
        itemName,

        noteId,
        status
    ]);

    
    const validate = useCallback<IFormValidate<PostNoteAdd, FormError>>(async (formState, formActions) => {
        let errors: Record<string, FormError[]> = {};

        if (!formState?.value?.title) {
            errors['title'] = [
                {
                    property: 'title',
                    message: 'Description must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        return errors;
    }, []);


    const submit = useCallback<IFormSubmit<PostNoteAdd, FormError>>(async (formState, formActions) => {
        await client.post(new PostNoteUpdate({ ...formState.value }));

        enqueueSnackbar(`Note successfully saved!`, { variant: "success" });

        onCancel?.();
        
        return {};
    }, [client, enqueueSnackbar, onCancel]);

    const submitFailed = useCallback<IFormSubmitFailed<PostNoteAdd, FormError>>(async (formState, formActions) => {
        
        enqueueSnackbar(`An error occurred while attempting to save the note!`, { variant: "error" });

        return {};
    }, [enqueueSnackbar]);

    const submitValidationFailed = useCallback<IFormSubmitValidationFailed<PostNoteAdd, FormError>>(async (formState, formActions, validationException) => {
        enqueueSnackbar("The form contains invalid data, please correct the marked fields and try again!", { variant: "warning" });

        return formState.errors;
    }, [enqueueSnackbar]);

    return (
        <Form
            initialValue={initialData}
            debug={AppSettings.debug}
            onSubmit={submit}
            onSubmitFailed={submitFailed}
            onSubmitValidationFailed={submitValidationFailed}
            onValidate={validate}
            validateOn={ValidateOn.onChange}
        >
            <FormContainer>   
                <Typography variant="h4" gutterBottom>
                    Update Note to {noteStatusToText(status)}
                </Typography>

                <InputOnlyField
                    propertyPath="title"
                    label="Description"
                    helperText="A description of the note or query."
                    margin="normal"
                    component={Text}
                />      
                <InputOnlyField
                    propertyPath="details"
                    label="Detailed Note"
                    helperText="The complete details of the note or query."
                    margin="normal"
                    component={Text}
                    multiline
                    minRows={2}
                />     
                <FormButtons>
                    <FormSubmitButton
                        variant="contained"
                        color="primary"
                    >
                        Save
                    </FormSubmitButton>
                    <Button
                        variant="contained"
                        color="inherit"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                </FormButtons>  
            </FormContainer>     
        </Form>
    )
}

export default NoteEditForm;